import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { getLocalizedString } from "@icarius-localization/strings";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import { createUserMenusAction, updateUserMenusAction } from "../../actions";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { GalleryDialog } from "@icarius-common/galleryDialog";
import GalleryButtonAndDisplay from "@icarius-common/galleryDialog/components/galleryButtonAndDisplay";
import TextFieldMultiline from "@icarius-common/textFieldMultiline";
import useHandleForm from "./useHandleForm";
import ReadOnlyInput from "@icarius-common/dynamicInputs/readOnlyInput";
import { roles } from "@icarius-utils/constants";
import ModulesAndMenusTree from "@icarius-pages/modulesAndMenus/components/modules/treeDialog/modulesAndMenusTree";
import { Alert } from "@material-ui/lab";
import CustomNumberFormat from '@icarius-common/dynamicInputs/customNumberFormat';

const gridStyle = { padding: "10px" };

const Form = ({ data, trees, handleClose }) => {

  const dispatch = useDispatch();

  const [imagesDialogIsOpen, setImagesDialogIsOpen] = useState(false);

  const create = (dataToSend) => {
    dispatch(createUserMenusAction(dataToSend))
      .then((res) => res?.status === 200 && handleClose());
  }

  const modify = (dataToSend) => {
    dispatch(updateUserMenusAction(dataToSend))
      .then((res) => res?.status === 200 && handleClose());
  }

  const openValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
  }

  const {
    isCreate,
    formData,
    setFormValue,
    submit
  } = useHandleForm(data, create, modify, openValidationError);

  const tree = trees[formData.role];

  const siblingRoute = useMemo(() => {
    if (!formData.siblingPath) return "";
    if (!tree?.modules?.length) return "";

    let aux = "";
    const searchInBranch = (branch) => {
      branch.forEach((item) => {
        if (item.path === formData.siblingPath) {
          aux = item;
        } else {
          if (item.menus) {
            searchInBranch(item.menus);
          }
        }
      })
    }

    searchInBranch(tree.modules);
    return aux;
  }, [tree.modules, formData.siblingPath])

  return (
    <>
      <Grid container item xs={12} direction="row" style={{ paddingTop: 10 }}>
        <Grid container item xs={12}>
          <Grid container item alignItems="center" xs={12} sm={3} style={gridStyle}>
            <TextField
              disabled={!Boolean(isCreate)}
              required
              fullWidth
              margin={"none"}
              label={getLocalizedString("functionCodeLong")}
              onChange={(e) => setFormValue(e.target.value, "functionCode")}
              value={formData.functionCode}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <Grid container item alignItems="center" xs={12} sm={9} style={gridStyle}>
            <TextField
              required
              fullWidth
              margin={"none"}
              label={getLocalizedString("functionNameLong")}
              value={formData.functionName}
              onChange={(e) => setFormValue(e.target.value, "functionName")}
              inputProps={{ maxLength: 60 }}
            />
          </Grid>
          <Grid container item alignItems="center" xs={12} sm={3} style={gridStyle}>
            <TextField
              required
              InputProps={{ inputComponent: CustomNumberFormat }}
              fullWidth
              margin={"none"}
              label={getLocalizedString("order")}
              onChange={(e) => setFormValue(e.target.value, "order")}
              value={formData.order}
              inputProps={{ min: "1", step: "1", max: 999, decimals: 0, maxLength: 3, isAllowed: (v) => v.floatValue === undefined || v.floatValue <= 999 }}
              onInput={e => {
                setFormValue(e.target.value, 'order');
              }}
            />
          </Grid>
          <Grid container item alignItems="center" xs={12} sm={9} style={gridStyle}>
            <TextField
              required
              fullWidth
              margin={"none"}
              label={getLocalizedString("menuName")}
              value={formData.name}
              onChange={(e) => setFormValue(e.target.value, "name")}
              inputProps={{ maxLength: 100 }}
            />
          </Grid>
          <Grid container item alignItems="center" xs={12} style={{ padding: 10 }}>
            <TextFieldMultiline
              fullWidth
              label={getLocalizedString("alternativeMenuName")}
              rows={5}
              margin={"none"}
              value={formData.sqlLang}
              onChange={(e) => setFormValue(e.target.value, "sqlLang")}
              inputProps={{ spellCheck: 'false' }}
              helperText={getLocalizedString("alternativeMenuNameHelperText")}
            />
          </Grid>
          <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
            <FormControl style={{ width: "100%" }}>
              <InputLabel id={'label-role'}>{getLocalizedString("role")}</InputLabel>
              <Select
                value={formData.role}
                labelId={'label-role'}
                id={'select-role'}
                onChange={(e) => setFormValue(e.target.value, "role")}
                margin={"none"}
              >
                {
                  Object.keys(roles).map((roleName) => ({ key: roles[roleName], value: getLocalizedString(roleName) }))
                    .map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}
                      >
                        {item.value}
                      </MenuItem>
                    ))
                }
              </Select>
            </FormControl>
          </Grid>
          <Grid container item direction="column" xs={12} sm={6} style={gridStyle}>
            <ReadOnlyInput
              label={
                <>
                  {getLocalizedString("previousMenuLocation")}
                  <span style={{ fontWeight: 400, fontSize: 16, marginLeft: 5 }} className="errorColor">
                    {"*"}
                  </span>
                </>
              }
              value={siblingRoute.name}
            />
          </Grid>
          <Grid container item direction="column" xs={12} style={gridStyle}>
            <div className="whiteText" style={{ marginTop: 10 }}>
              <Alert variant="filled" severity="info" style={{ marginBottom: 10 }}>
                {getLocalizedString("clickToSelectMenu")}
              </Alert>
              <ModulesAndMenusTree
                data={tree}
                selectedIds={[siblingRoute.id]}
                handleClick={(newMenu) => setFormValue(newMenu.path, "siblingPath")}
              />
            </div>
          </Grid>
          <GalleryButtonAndDisplay
            image={formData.image}
            handleOpenGallery={() => setImagesDialogIsOpen(true)}
          />
        </Grid>
        <Grid container item xs={12} justify={"flex-end"} alignItems="center" style={{ paddingBottom: 20 }}>
          <ButtonDialogAction isAccept onClick={submit} text={getLocalizedString("save")} />
        </Grid>
      </Grid>
      {
        imagesDialogIsOpen &&
        <GalleryDialog
          open={imagesDialogIsOpen}
          initialValue={formData.image}
          type={"news"}
          handleSubmit={(image) => setFormValue(image, "image")}
          handleClose={() => setImagesDialogIsOpen(false)}
        />
      }
    </>
  );
}

export default Form;